import React from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Checkbox from '@mui/joy/Checkbox';
import Chip from '@mui/joy/Chip';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import Link from '@mui/joy/Link';
import Option from '@mui/joy/Option';
import Select from '@mui/joy/Select';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Table from '@mui/joy/Table';
import Textarea from '@mui/joy/Textarea';
import Tooltip, { TooltipProps } from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { NotificationService } from '../../common/notification';
import { formatDateTime } from '../../common/dateTime';
import { getBlockRuleLabels } from '../../models/userBlock';
import {
    AutoHoldAstNode,
    AutoHoldCaseRow,
    AutoHoldCaseStatus,
    AutoHoldDictionaryRow,
    AutoHoldHitRow,
    AutoHoldKpi,
    AutoHoldPreviewRow,
    AutoHoldRuleMode,
    AutoHoldRuleRow,
    AutoHoldSignalSchema,
    AutoHoldWhitelistRow,
    emptyAndGroup,
    emptyCondition,
    autoHoldCmpCode,
    autoHoldCmpLabel,
} from '../../models/autoHold';
import { useLocation } from 'react-router-dom';
import ConfirmDialog from '../../components/confirmDialog/ConfirmDialog';
import { NavigationTabs } from '../../components/tab/NavigationTabs';
import {
    autoHoldAddWhitelistApi,
    autoHoldApplyAllApi,
    autoHoldApplyHitApi,
    autoHoldHitFalsePositiveApi,
    autoHoldBackfillApi,
    autoHoldCasesApi,
    autoHoldDeleteRuleApi,
    autoHoldDictionariesApi,
    autoHoldHitsApi,
    autoHoldKpiApi,
    autoHoldPreviewApi,
    autoHoldPreviewAllApi,
    autoHoldRemoveWhitelistApi,
    autoHoldRulesApi,
    autoHoldSaveDictionaryApi,
    autoHoldSaveRuleApi,
    autoHoldSignalsApi,
    autoHoldVerdictApi,
    autoHoldWhitelistApi,
} from './autoHoldApi';

/** Ссылка на карточку пользователя. */
function userLink(id: string | number) {
    return (
        <Link href={`/app/users/${id}`} target="_blank" rel="noreferrer" underline="hover">
            {String(id)}
        </Link>
    );
}

/** Имена правил, из‑за которых человека положили в очередь. */
function caseRuleNames(c: AutoHoldCaseRow, rules: AutoHoldRuleRow[]): string[] {
    try {
        const ev = JSON.parse(c.evidenceJson || '{}') as {
            rules?: Array<{ RuleName?: string; ruleName?: string }>;
        };
        const fromEvidence = (ev.rules ?? [])
            .map(r => r.RuleName ?? r.ruleName)
            .filter((name): name is string => Boolean(name));
        if (fromEvidence.length) {
            return fromEvidence;
        }
    } catch {
        /* строка evidence не JSON — ниже по идентификаторам */
    }
    return (c.triggeredRuleIds ?? []).map(id => rules.find(r => r.id === id)?.name ?? `правило ${id}`);
}

/** Чипы флагов блокировки, которые повесил автохолд. */
function flagsChips(flags: number) {
    if (!flags) {
        return <Typography level="body-xs">не применялось</Typography>;
    }
    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {getBlockRuleLabels().filter(r => (flags & r.value) === r.value).map(r => (
                <Chip key={r.value} size="sm" variant="soft">{r.label}</Chip>
            ))}
        </Box>
    );
}

/** Выше сайдбара (zIndex: 10000), иначе длинный тултип уходит под меню. */
const TOOLTIP_Z_INDEX = 10001;

/** Подпись поля с тултипом. */
function FieldHint(props: { label?: string; hint: React.ReactNode }) {
    const title = typeof props.hint === 'string'
        ? <Typography level="body-sm" sx={{ whiteSpace: 'normal' }}>{props.hint}</Typography>
        : props.hint;
    const tooltip = (child: React.ReactElement) => (
        <Tooltip
            title={title}
            variant="outlined"
            placement="top-start"
            disablePortal={false}
            slotProps={{
                root: { sx: { zIndex: TOOLTIP_Z_INDEX, maxWidth: 380 } },
            } as TooltipProps['slotProps']}
        >
            {child}
        </Tooltip>
    );
    if (!props.label) {
        return tooltip(
            <IconButton size="sm" variant="plain" sx={{ '--IconButton-size': '1.5rem' }}>
                <InfoOutlinedIcon sx={{ fontSize: 14, opacity: 0.55 }} />
            </IconButton>
        );
    }
    return tooltip(
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'help',
                borderBottom: '1px dotted',
                borderColor: 'neutral.outlinedBorder',
            }}
        >
            {props.label}
            <InfoOutlinedIcon sx={{ fontSize: 14, opacity: 0.55 }} />
        </Box>
    );
}

function labeledHint(label: string, description: string | undefined, required?: boolean) {
    const text = `${label}${required ? ' *' : ''}`;
    return description ? <FieldHint label={text} hint={description} /> : text;
}

/** Какая вкладка автохолда открыта по якорю. */
function autoHoldInnerTab(hash: string): 'queue' | 'rules' | 'whitelist' | 'dictionaries' {
    const slug = (hash || '').replace('#', '').trim().toLowerCase();
    if (slug === 'autohold-rules') {
        return 'rules';
    }
    if (slug === 'autohold-whitelist') {
        return 'whitelist';
    }
    if (slug === 'autohold-dictionaries') {
        return 'dictionaries';
    }
    return 'queue';
}

/** Кнопка с подсказкой при наведении. */
function HintButton({ hint, ...props }: { hint: string } & React.ComponentProps<typeof Button>) {
    return (
        <Tooltip
            title={<Typography level="body-sm" sx={{ whiteSpace: 'normal' }}>{hint}</Typography>}
            variant="outlined"
            placement="top-start"
            disablePortal={false}
            slotProps={{
                root: { sx: { zIndex: TOOLTIP_Z_INDEX, maxWidth: 360 } },
            } as TooltipProps['slotProps']}
        >
            <Button {...props} />
        </Tooltip>
    );
}

/** Снимок полей редактора, чтобы понять, есть ли несохранённые правки. */
function ruleSnapshot(rule: AutoHoldRuleRow, ast: AutoHoldAstNode): string {
    return JSON.stringify({
        name: rule.name,
        enabled: rule.enabled,
        mode: rule.mode,
        actionFlags: rule.actionFlags,
        root: ast,
    });
}

/** Тон строки: боевое вкл — зелёный, тестовое вкл — жёлтый, выкл — красный. */
function ruleRowTone(rule: AutoHoldRuleRow): 'enforce' | 'test' | 'off' {
    if (!rule.enabled) {
        return 'off';
    }
    return rule.mode === AutoHoldRuleMode.Enforce ? 'enforce' : 'test';
}

/** Черновик нового правила: в БД ещё не пишем, пока не нажмут «Сохранить». */
function blankRule(): AutoHoldRuleRow {
    return {
        id: 0,
        name: 'Новое правило',
        enabled: false,
        mode: AutoHoldRuleMode.Test,
        priority: 0,
        actionFlags: 13,
        rootJson: JSON.stringify(emptyAndGroup()),
        updated: '',
        hits: 0,
        confirmed: 0,
        falsePositive: 0,
    };
}

type DictionaryMeta = {
    title: string;
    usedIn: string;
    purpose: string;
    hint: React.ReactNode;
    format: string;
    placeholder: string;
};

/** Подписи справочников: название, где читается, зачем. */
function dictionaryMeta(key: string): DictionaryMeta {
    const known: Record<string, DictionaryMeta> = {
        hostingAsns: {
            title: 'Сети датацентров и VPN',
            usedIn: 'Условие «IP датацентра / VPN», рычаг «сверять номер сети».',
            purpose: 'Если номер сети (ASN) IP последнего пополнения есть в списке, условие считает, что человек зашёл с датацентра или VPN.',
            hint: (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography level="body-sm">
                        В список ставьте номер сети, не название компании. Номер смотрите у пополнения в определении сети по адресу подключения.
                    </Typography>
                    <Typography level="body-sm">
                        Работает вместе со справочником «Слова VPN и хостинга в названии сети»: можно ловить и по номеру, и по тексту.
                    </Typography>
                </Box>
            ),
            format: 'Только цифры, по одному номеру на строку. Например 16509, 15169, 13335.',
            placeholder: '16509\n15169\n13335',
        },
        orgKeywords: {
            title: 'Слова VPN и хостинга в названии сети',
            usedIn: 'Условие «IP датацентра / VPN», рычаги «искать слова VPN и хостинга» и «сверять название организации».',
            purpose: 'Если в названии организации сети есть такое слово (vpn, hosting, cloudflare), условие тоже считает подключение датацентром или VPN — даже когда номера сети нет в списке выше.',
            hint: (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography level="body-sm">
                        Куски текста без учёта регистра. Короткое общее слово даст лишние срабатывания — лучше писать узнаваемые куски названий.
                    </Typography>
                </Box>
            ),
            format: 'По одному слову или куску названия на строку, латиница. Регистр не важен.',
            placeholder: 'vpn\nhosting\ncloudflare',
        },
        fiatCurrencies: {
            title: 'Валюты, которые не считаем криптой',
            usedIn: 'Условие «Последний депозит криптой».',
            purpose: 'Валюта последнего пополнения из этого списка — обычные деньги. Если валюты нет в списке или кошелёк помечен как криптовалютный, условие даёт «да».',
            hint: (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography level="body-sm">
                        Список общий для всех правил. Пустой список — смотрим только пометку кошелька, без сверки кода валюты.
                    </Typography>
                </Box>
            ),
            format: 'Трёхбуквенный код валюты, по одному на строку, заглавными буквами. Рубль — RUB, доллар — USD, тенге — KZT, евро — EUR.',
            placeholder: 'RUB\nUSD\nKZT\nEUR',
        },
    };
    return known[key] ?? {
        title: 'Список значений',
        usedIn: 'Общий список для условий правил.',
        purpose: 'После сохранения правила читают новые значения на следующей проверке.',
        hint: 'Каждое значение с новой строки. Пустые строки отбрасываются.',
        format: 'По одному значению на строку. Пустые строки отбрасываются.',
        placeholder: '',
    };
}

/** Достаёт SteamId64 из сырого ввода: цифры, список, ссылка на профиль. */
function normalizeSteamIds(raw: string): string {
    return raw
        .split(/[\s,;]+/)
        .map(part => {
            const fromUrl = part.match(/7656119\d{10}/);
            if (fromUrl) {
                return fromUrl[0];
            }
            const digits = part.replace(/\D/g, '');
            return digits.length === 17 ? digits : part.trim();
        })
        .filter(Boolean)
        .join('\n');
}

/** Человекочитаемая формула дерева: (A И B) ИЛИ C. */
function formula(node: AutoHoldAstNode | undefined, signals: AutoHoldSignalSchema[]): string {
    if (!node) return '';
    if (node.op && node.children) {
        const joiner = node.op.toLowerCase() === 'or' ? ' ИЛИ ' : ' И ';
        return `(${node.children.map(c => formula(c, signals)).join(joiner)})`;
    }
    const name = signals.find(s => s.id === node.signal)?.displayName ?? node.signal ?? '?';
    return `${name} ${autoHoldCmpLabel(node.cmp)}`.trim();
}

/** Чекбокс для bool-рычага: в AST по-прежнему строки true/false. */
function paramIsTrue(value: unknown): boolean {
    if (typeof value === 'boolean') {
        return value;
    }
    const raw = String(value ?? '').trim().toLowerCase();
    return raw === 'true' || raw === '1';
}

/** Конструктор дерева условий: группы И/ИЛИ и сигналы с рычагами. */
function AstEditor(props: {
    node: AutoHoldAstNode;
    depth: number;
    signals: AutoHoldSignalSchema[];
    onChange: (n: AutoHoldAstNode) => void;
    onRemove?: () => void;
}) {
    const { node, depth, signals, onChange } = props;
    if (node.op) {
        const children = node.children ?? [];
        return (
            <Sheet variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 8 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Select
                        size="sm"
                        value={node.op}
                        onChange={(_, v) => {
                            if (v == null || v === node.op) {
                                return;
                            }
                            onChange({ ...node, op: String(v) });
                        }}
                    >
                        <Option value="And">Все (И)</Option>
                        <Option value="Or">Любое (ИЛИ)</Option>
                    </Select>
                    <FieldHint hint="Все (И) — срабатывает, только если каждое вложенное условие истинно. Любое (ИЛИ) — достаточно одного. Пустая группа не срабатывает. Глубина дерева не больше 4." />
                    {depth < 4 && (
                        <>
                            <HintButton
                                size="sm"
                                variant="outlined"
                                hint="Добавляет сигнал с рычагами. Сравнение и значение задаются справа."
                                onClick={() => {
                                const first = signals[0];
                                onChange({
                                    ...node,
                                    children: [...children, emptyCondition(first?.id ?? 'IsFirstSuccessfulPayment', first)],
                                });
                            }}>Условие</HintButton>
                            <HintButton
                                size="sm"
                                variant="outlined"
                                hint="Вложенная группа И/ИЛИ. Глубина дерева не больше 4."
                                onClick={() => onChange({
                                ...node,
                                children: [...children, emptyAndGroup()],
                            })}>Группа</HintButton>
                        </>
                    )}
                    {props.onRemove && (
                        <HintButton size="sm" color="danger" variant="plain" hint="Убирает эту группу и всё внутри." onClick={props.onRemove}>
                            Удалить
                        </HintButton>
                    )}
                </Stack>
                {children.map((child, i) => (
                    <AstEditor
                        key={i}
                        node={child}
                        depth={depth + 1}
                        signals={signals}
                        onChange={(n) => {
                            const next = [...children];
                            next[i] = n;
                            onChange({ ...node, children: next });
                        }}
                        onRemove={() => onChange({ ...node, children: children.filter((_, j) => j !== i) })}
                    />
                ))}
            </Sheet>
        );
    }

    const schema = signals.find(s => s.id === node.signal);
    const ops = schema?.operators ?? [];
    return (
        <Sheet variant="soft" sx={{ p: 1.5, mb: 1, borderRadius: 8 }}>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                    <Select
                        size="sm"
                        value={node.signal ?? ''}
                        onChange={(_, v) => {
                            if (v == null || v === node.signal) {
                                return;
                            }
                            const id = String(v);
                            const next = signals.find(s => s.id === id);
                            onChange({
                                ...node,
                                signal: id,
                                cmp: emptyCondition(id, next).cmp,
                                value: undefined,
                                params: emptyCondition(id, next).params,
                            });
                        }}
                        sx={{ minWidth: 260 }}
                    >
                        {signals.map(s => (
                            <Option key={s.id} value={s.id} title={s.description}>{s.displayName}</Option>
                        ))}
                    </Select>
                    {schema?.description ? <FieldHint hint={schema.description} /> : null}
                    <Select
                        size="sm"
                        value={node.cmp ?? ''}
                        onChange={(_, v) => {
                            if (v == null || v === node.cmp) {
                                return;
                            }
                            onChange({ ...node, cmp: String(v) });
                        }}
                    >
                        {ops.map(op => {
                            const code = autoHoldCmpCode(op);
                            return <Option key={op} value={code}>{autoHoldCmpLabel(code)}</Option>;
                        })}
                    </Select>
                    {schema?.valueKind !== 1 && (
                        <Input
                            size="sm"
                            placeholder={schema?.unit === 'count' ? 'количество' : schema?.unit === 'percent' ? '% порог' : 'порог / список'}
                            value={Array.isArray(node.value) ? (node.value as string[]).join(',') : String(node.value ?? '')}
                            onChange={e => {
                                const raw = e.target.value;
                                const isList = node.cmp === 'In' || node.cmp === 'NotIn';
                                onChange({
                                    ...node,
                                    value: isList
                                        ? raw.split(',').map(x => x.trim()).filter(Boolean)
                                        : (Number.isFinite(Number(raw)) && raw !== '' ? Number(raw) : raw),
                                });
                            }}
                        />
                    )}
                    {props.onRemove && (
                        <HintButton size="sm" color="danger" variant="plain" hint="Убирает это условие из группы." onClick={props.onRemove}>
                            Удалить
                        </HintButton>
                    )}
                </Stack>
                {(schema?.params ?? []).map(p => p.type === 'bool' ? (
                    <Stack key={p.key} direction="row" spacing={0.5} alignItems="center">
                        <Checkbox
                            size="sm"
                            label={`${p.label}${p.required ? ' *' : ''}`}
                            checked={paramIsTrue(node.params?.[p.key] ?? p.seedDefault)}
                            onChange={e => onChange({
                                ...node,
                                params: { ...(node.params ?? {}), [p.key]: e.target.checked ? 'true' : 'false' },
                            })}
                        />
                        {p.description ? <FieldHint hint={p.description} /> : null}
                    </Stack>
                ) : (
                    <FormControl key={p.key} size="sm">
                        <FormLabel>{labeledHint(p.label, p.description, p.required)}</FormLabel>
                        <Input
                            size="sm"
                            placeholder={p.key === 'hashName' ? 'пусто = любой скин' : (p.seedDefault ?? '')}
                            value={String(node.params?.[p.key] ?? '')}
                            onChange={e => onChange({
                                ...node,
                                params: { ...(node.params ?? {}), [p.key]: e.target.value },
                            })}
                        />
                    </FormControl>
                ))}
            </Stack>
        </Sheet>
    );
}

/** Вкладка «Автохолд»: KPI, очередь, правила, вайтлист, справочники. */
export function AutoHoldPanel() {
    const location = useLocation();
    const [kpi, setKpi] = React.useState<AutoHoldKpi | null>(null);
    const [cases, setCases] = React.useState<AutoHoldCaseRow[]>([]);
    const [rules, setRules] = React.useState<AutoHoldRuleRow[]>([]);
    const [signals, setSignals] = React.useState<AutoHoldSignalSchema[]>([]);
    const [selected, setSelected] = React.useState<AutoHoldRuleRow | null>(null);
    const [root, setRoot] = React.useState<AutoHoldAstNode>(emptyAndGroup());
    const [hits, setHits] = React.useState<AutoHoldHitRow[]>([]);
    const [previewText, setPreviewText] = React.useState('');
    const [preview, setPreview] = React.useState<AutoHoldPreviewRow[]>([]);
    const [whitelist, setWhitelist] = React.useState<AutoHoldWhitelistRow[]>([]);
    const [wlUser, setWlUser] = React.useState('');
    const [wlReason, setWlReason] = React.useState('');
    const [dicts, setDicts] = React.useState<AutoHoldDictionaryRow[]>([]);
    const [applyAllOpen, setApplyAllOpen] = React.useState(false);
    const [runAllOpen, setRunAllOpen] = React.useState(false);
    const [decisionConfirm, setDecisionConfirm] = React.useState<{
        title: string;
        body: string;
        run: () => Promise<void>;
        extraLabel?: string;
        extraRun?: () => Promise<void>;
    } | null>(null);
    const [unsavedOpen, setUnsavedOpen] = React.useState(false);
    const [unsavedRuleName, setUnsavedRuleName] = React.useState('');
    const [baseline, setBaseline] = React.useState('');
    const pendingNav = React.useRef<'create' | AutoHoldRuleRow | null>(null);
    const navigatingRef = React.useRef(false);
    const selectedRef = React.useRef<AutoHoldRuleRow | null>(null);
    const rootRef = React.useRef(root);
    const baselineRef = React.useRef(baseline);
    selectedRef.current = selected;
    rootRef.current = root;
    baselineRef.current = baseline;

    const isDirty = () => selected != null && ruleSnapshot(selected, root) !== baseline;

    const loadQueue = React.useCallback(async (alive: () => boolean = () => true) => {
        const [k, c] = await Promise.all([autoHoldKpiApi(), autoHoldCasesApi(1, 50, 0)]);
        if (!alive()) {
            return;
        }
        if (k.result) setKpi(k.value!);
        if (c.result) setCases(c.value!.items);
    }, []);

    const loadRules = React.useCallback(async (alive: () => boolean = () => true) => {
        const [r, s] = await Promise.all([autoHoldRulesApi(), autoHoldSignalsApi()]);
        if (!alive()) {
            return [] as AutoHoldRuleRow[];
        }
        const list = r.result ? (r.value ?? []) : [];
        if (r.result) setRules(list);
        if (s.result) setSignals(s.value ?? []);
        return list;
    }, []);

    const loadWhitelist = React.useCallback(async (alive: () => boolean = () => true) => {
        const r = await autoHoldWhitelistApi(1, 50);
        if (!alive()) {
            return;
        }
        if (r.result) setWhitelist(r.value!.items);
    }, []);

    const loadDicts = React.useCallback(async (alive: () => boolean = () => true) => {
        const r = await autoHoldDictionariesApi();
        if (!alive()) {
            return;
        }
        if (r.result) setDicts(r.value ?? []);
    }, []);

    const loadHits = React.useCallback(async (ruleId: number, alive: () => boolean = () => true) => {
        const h = await autoHoldHitsApi(ruleId);
        if (!alive()) {
            return;
        }
        if (h.result) setHits(h.value ?? []);
    }, []);

    React.useEffect(() => {
        let cancelled = false;
        const alive = () => !cancelled;
        const tab = autoHoldInnerTab(location.hash);

        const run = async () => {
            if (tab === 'whitelist') {
                await loadWhitelist(alive);
                return;
            }
            if (tab === 'dictionaries') {
                await loadDicts(alive);
                return;
            }
            if (tab === 'rules') {
                const list = await loadRules(alive);
                if (!alive()) {
                    return;
                }
                const cur = selectedRef.current;
                if (!cur?.id) {
                    return;
                }
                const fresh = list.find(x => x.id === cur.id);
                if (fresh && ruleSnapshot(cur, rootRef.current) === baselineRef.current) {
                    setSelected(fresh);
                }
                await loadHits(cur.id, alive);
                return;
            }
            await Promise.all([loadQueue(alive), loadRules(alive)]);
        };

        run().catch(console.error);
        return () => {
            cancelled = true;
        };
    }, [location.hash, loadQueue, loadRules, loadWhitelist, loadDicts, loadHits]);

    const applyOpen = async (rule: AutoHoldRuleRow) => {
        setSelected(rule);
        let ast = emptyAndGroup();
        try {
            ast = JSON.parse(rule.rootJson);
        } catch {
            ast = emptyAndGroup();
        }
        setRoot(ast);
        setBaseline(ruleSnapshot(rule, ast));
        if (rule.id) {
            await loadHits(rule.id);
        } else {
            setHits([]);
        }
        setPreview([]);
        setPreviewText('');
        // Joy Select может дернуть onChange при монтировании — не считаем это правкой.
        window.setTimeout(() => {
            const current = selectedRef.current;
            if (current && current.id === rule.id) {
                setBaseline(ruleSnapshot(current, rootRef.current));
            }
        }, 0);
    };

    const askUnsaved = (next: 'create' | AutoHoldRuleRow) => {
        pendingNav.current = next;
        setUnsavedRuleName(selected?.name ?? 'правиле');
        setUnsavedOpen(true);
    };

    const openDraft = () => {
        void applyOpen(blankRule());
    };

    const openRule = async (rule: AutoHoldRuleRow) => {
        if (unsavedOpen || navigatingRef.current) {
            return;
        }
        if (selected && selected.id !== rule.id && isDirty()) {
            askUnsaved(rule);
            return;
        }
        await applyOpen(rule);
    };

    const finishPendingNav = async (persist: boolean) => {
        const next = pendingNav.current;
        if (persist) {
            const ok = await saveRule();
            if (!ok) {
                return;
            }
        }
        pendingNav.current = null;
        navigatingRef.current = true;
        window.setTimeout(() => {
            const done = async () => {
                try {
                    if (next === 'create') {
                        openDraft();
                        return;
                    }
                    if (next) {
                        await applyOpen(next);
                    }
                } finally {
                    navigatingRef.current = false;
                }
            };
            void done();
        }, 0);
    };

    const runPreview = async () => {
        if (!selected?.id) {
            return;
        }
        const steamIds = normalizeSteamIds(previewText);
        if (!steamIds) {
            NotificationService.alertError('Введите SteamId');
            return;
        }
        setPreviewText(steamIds);
        const r = await autoHoldPreviewApi(selected.id, steamIds);
        if (r.result) {
            setPreview(r.value ?? []);
            await loadHits(selected.id);
        } else {
            NotificationService.alertError('Прогон не удался');
        }
    };

    const saveRule = async (): Promise<boolean> => {
        if (!selected) return false;
        const result = await autoHoldSaveRuleApi({
            ...(selected.id ? { id: selected.id } : {}),
            name: selected.name,
            enabled: selected.enabled,
            mode: selected.mode,
            priority: selected.priority,
            actionFlags: selected.actionFlags,
            rootJson: JSON.stringify(root),
        });
        if (result.result) {
            NotificationService.alertSuccess('Правило сохранено');
            await loadRules();
            const saved = result.value!;
            setSelected(saved);
            let ast = root;
            try {
                ast = JSON.parse(saved.rootJson);
                setRoot(ast);
            } catch {
                ast = root;
            }
            setBaseline(ruleSnapshot(saved, ast));
            return true;
        }
        NotificationService.alertError('Не удалось сохранить правило');
        return false;
    };

    const createRule = () => {
        if (unsavedOpen || navigatingRef.current) {
            return;
        }
        if (selected && isDirty()) {
            askUnsaved('create');
            return;
        }
        openDraft();
    };

    return (
        <Box>
            <NavigationTabs
                tabs={[
                    {
                        tabSlug: 'autohold',
                        tabTitle: 'Очередь',
                        tabContent: (
                            <>
                    <Typography level="title-sm" sx={{ mb: 0.5 }}>
                        <FieldHint
                            label="Разбор автохолда"
                            hint="Сюда попадает человек, на которого сработало боевое правило. Блок уже стоит. Вы решаете: оставить блок или система ошиблась и снять только автохолд. Тестовые правила в эту очередь не попадают."
                        />
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 2 }} color="neutral">
                        Список ниже — кто ещё ждёт решения. Счётчики сверху — за сегодня (UTC): сколько холдов поставили, сколько уже разобрали и на какую сумму ещё висит блок.
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
                        <Chip>
                            <FieldHint
                                label={`Холдов сегодня: ${kpi?.holds ?? 0}`}
                                hint="Сколько людей автохолд положил в очередь за текущие сутки UTC. И ждут, и уже разобранные."
                            />
                        </Chip>
                        <Chip color="warning">
                            <FieldHint
                                label={`Ждут решения: ${kpi?.pending ?? 0}`}
                                hint="Сколько кейсов ещё открыты. Это те же люди, что в таблице ниже."
                            />
                        </Chip>
                        <Chip color="success">
                            <FieldHint
                                label={`Оставили блок: ${kpi?.confirmed ?? 0}`}
                                hint="Сегодня нажали «Оставить блок»: холд подтвердили, блок не снимали."
                            />
                        </Chip>
                        <Chip color="danger">
                            <FieldHint
                                label={`Ошибка: ${kpi?.falsePositive ?? 0}`}
                                hint="Сегодня нажали «Ошибка»: срабатывание ложное. Блок снимали только если выбрали «Да, и снять блокировку»."
                            />
                        </Chip>
                        <Chip>
                            <FieldHint
                                label={`Сумма в ожидании: ${((kpi?.blockedAmount ?? 0) / 100).toLocaleString('ru-RU')} ₽`}
                                hint="Сумма последних успешных пополнений по кейсам, которые ещё ждут решения. В копейках в базе, здесь в рублях."
                            />
                        </Chip>
                        <HintButton
                            size="sm"
                            variant="outlined"
                            hint="Прогоняет все включённые правила по людям, у кого уже стоит любая блокировка. Новые кейсы появятся, если боевое правило сработает. Нужно редко: догнать старых заблокированных, не ждать нового пополнения."
                            onClick={() => autoHoldBackfillApi().then(() => NotificationService.alertSuccess('Прогон по текущим блокировкам поставлен в очередь'))}
                        >
                            Догнать уже заблокированных
                        </HintButton>
                    </Stack>
                    <Sheet variant="outlined">
                        <Table>
                            <thead>
                            <tr>
                                <th>
                                    <FieldHint
                                        label="Кто"
                                        hint="SteamId. Ссылка открывает карточку пользователя."
                                    />
                                </th>
                                <th>
                                    <FieldHint
                                        label="Почему в очереди"
                                        hint="Какие боевые правила сработали. Несколько правил у одного человека — нормально: флаги складываются."
                                    />
                                </th>
                                <th>
                                    <FieldHint
                                        label="Сумма"
                                        hint="Последнее успешное пополнение на момент холда. Ориентир, не баланс счёта."
                                    />
                                </th>
                                <th>
                                    <FieldHint
                                        label="Когда"
                                        hint="Когда автохолд поставил блок и открыл кейс."
                                    />
                                </th>
                                <th>
                                    <FieldHint
                                        label="Решение"
                                        hint="Оставить блок — холд верный. Ошибка — срабатывание ложное, блок можно снять отдельной кнопкой в окне. Вайтлист — больше этого человека не оцениваем."
                                    />
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {cases.length === 0 && (
                                <tr>
                                    <td colSpan={5}>
                                        <Typography level="body-sm" color="neutral" sx={{ py: 2 }}>
                                            Сейчас никто не ждёт разбора. Сюда попадают люди, на которых сработало включённое боевое правило.
                                        </Typography>
                                    </td>
                                </tr>
                            )}
                            {cases.map(c => {
                                const why = caseRuleNames(c, rules);
                                return (
                                <tr key={c.id}>
                                    <td>{userLink(c.userId)}</td>
                                    <td>
                                        {why.length
                                            ? why.join(', ')
                                            : <Typography level="body-sm" color="neutral">правило не указано</Typography>}
                                    </td>
                                    <td>{(c.blockedAmount / 100).toLocaleString('ru-RU')} ₽</td>
                                    <td>{formatDateTime(c.created)}</td>
                                    <td>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            <HintButton
                                                size="sm"
                                                color="danger"
                                                hint="Холд верный: блок оставляем как есть. Человек исчезает из очереди."
                                                onClick={() => setDecisionConfirm({
                                                    title: 'Оставить блок?',
                                                    body: `Холд по ${c.userId} верный: блок оставляем как есть. Человек исчезнет из очереди.`,
                                                    run: async () => {
                                                        await autoHoldVerdictApi(c.id, 1, 'оставить блок', false);
                                                        await loadQueue();
                                                    },
                                                })}
                                            >
                                                Оставить блок
                                            </HintButton>
                                            <HintButton
                                                size="sm"
                                                variant="outlined"
                                                hint="Система ошиблась: помечаем срабатывание. Блок снимем, только если в окне нажать «Да, и снять блокировку». Ручные блокировки (например продажа) не трогаем."
                                                onClick={() => setDecisionConfirm({
                                                    title: 'Отметить как ошибку?',
                                                    body: `Пометим холд ${c.userId} как ошибку. Ограничения останутся. Чтобы снять флаги этого автохолда — «Да, и снять блокировку». Ручные блокировки не трогаем.`,
                                                    run: async () => {
                                                        await autoHoldVerdictApi(c.id, 2, 'ошибка', false, false);
                                                        await loadQueue();
                                                    },
                                                    extraLabel: 'Да, и снять блокировку',
                                                    extraRun: async () => {
                                                        await autoHoldVerdictApi(c.id, 2, 'ошибка', false, true);
                                                        await loadQueue();
                                                    },
                                                })}
                                            >
                                                Ошибка
                                            </HintButton>
                                            <HintButton
                                                size="sm"
                                                variant="plain"
                                                hint="Как «Ошибка», плюс человека больше не оцениваем: в вайтлист. Новые правила на него не сработают."
                                                onClick={() => setDecisionConfirm({
                                                    title: 'Ошибка и в вайтлист?',
                                                    body: `Пометим холд ${c.userId} как ошибку и добавим в вайтлист. Ограничения останутся, пока не нажмёте «Да, и снять блокировку». Ручные блокировки не трогаем.`,
                                                    run: async () => {
                                                        await autoHoldVerdictApi(c.id, 2, 'ошибка + вайтлист', true, false);
                                                        await loadQueue();
                                                    },
                                                    extraLabel: 'Да, и снять блокировку',
                                                    extraRun: async () => {
                                                        await autoHoldVerdictApi(c.id, 2, 'ошибка + вайтлист', true, true);
                                                        await loadQueue();
                                                    },
                                                })}
                                            >
                                                Ошибка + вайтлист
                                            </HintButton>
                                        </Stack>
                                    </td>
                                </tr>
                                );
                            })}
                            </tbody>
                        </Table>
                    </Sheet>
                            </>
                        ),
                    },
                    {
                        tabSlug: 'autohold-rules',
                        tabTitle: 'Правила',
                        tabContent: (
                            <>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <HintButton
                            size="sm"
                            hint="Открывает черновик «Новое правило». Если в текущем есть несохранённые правки — спросит, сохранить ли."
                            onClick={() => createRule()}
                        >
                            Создать
                        </HintButton>
                    </Stack>
                    <Sheet variant="outlined" sx={{ mb: 2 }}>
                        <Table
                            hoverRow
                            sx={{
                                '--TableRow-hoverBackground': 'transparent',
                                '& tbody tr[data-tone="enforce"] td': {
                                    bgcolor: 'success.softBg',
                                    color: 'success.softColor',
                                },
                                '& tbody tr[data-tone="enforce"]:hover td': {
                                    bgcolor: 'success.softHoverBg',
                                },
                                '& tbody tr[data-tone="enforce"][data-selected="true"] td': {
                                    bgcolor: 'success.softHoverBg',
                                    color: 'success.solidBg',
                                    fontWeight: 600,
                                    boxShadow: 'inset 0 2px 0 var(--joy-palette-success-solidBg), inset 0 -2px 0 var(--joy-palette-success-solidBg)',
                                },
                                '& tbody tr[data-tone="enforce"][data-selected="true"] td:first-of-type': {
                                    boxShadow: 'inset 4px 0 0 var(--joy-palette-success-solidBg), inset 0 2px 0 var(--joy-palette-success-solidBg), inset 0 -2px 0 var(--joy-palette-success-solidBg)',
                                },
                                '& tbody tr[data-tone="test"] td': {
                                    bgcolor: 'warning.softBg',
                                    color: 'warning.softColor',
                                },
                                '& tbody tr[data-tone="test"]:hover td': {
                                    bgcolor: 'warning.softHoverBg',
                                },
                                '& tbody tr[data-tone="test"][data-selected="true"] td': {
                                    bgcolor: 'warning.softHoverBg',
                                    color: 'warning.solidBg',
                                    fontWeight: 600,
                                    boxShadow: 'inset 0 2px 0 var(--joy-palette-warning-solidBg), inset 0 -2px 0 var(--joy-palette-warning-solidBg)',
                                },
                                '& tbody tr[data-tone="test"][data-selected="true"] td:first-of-type': {
                                    boxShadow: 'inset 4px 0 0 var(--joy-palette-warning-solidBg), inset 0 2px 0 var(--joy-palette-warning-solidBg), inset 0 -2px 0 var(--joy-palette-warning-solidBg)',
                                },
                                '& tbody tr[data-tone="off"] td': {
                                    bgcolor: 'danger.softBg',
                                    color: 'danger.softColor',
                                },
                                '& tbody tr[data-tone="off"]:hover td': {
                                    bgcolor: 'danger.softHoverBg',
                                },
                                '& tbody tr[data-tone="off"][data-selected="true"] td': {
                                    bgcolor: 'danger.softHoverBg',
                                    color: 'danger.solidBg',
                                    fontWeight: 600,
                                    boxShadow: 'inset 0 2px 0 var(--joy-palette-danger-solidBg), inset 0 -2px 0 var(--joy-palette-danger-solidBg)',
                                },
                                '& tbody tr[data-tone="off"][data-selected="true"] td:first-of-type': {
                                    boxShadow: 'inset 4px 0 0 var(--joy-palette-danger-solidBg), inset 0 2px 0 var(--joy-palette-danger-solidBg), inset 0 -2px 0 var(--joy-palette-danger-solidBg)',
                                },
                            }}
                        >
                            <thead>
                            <tr>
                                <th>Имя</th>
                                <th>Вкл</th>
                                <th>Режим</th>
                                <th>Хиты / верно / ошибки</th>
                                <th>Обновлено</th>
                            </tr>
                            </thead>
                            <tbody>
                            {rules.map(r => (
                                <tr
                                    key={r.id}
                                    data-tone={ruleRowTone(r)}
                                    data-selected={selected?.id && selected.id === r.id ? 'true' : undefined}
                                    onClick={() => openRule(r).catch(console.error)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>{r.name}</td>
                                    <td>{r.enabled ? 'да' : 'нет'}</td>
                                    <td>{r.mode === AutoHoldRuleMode.Enforce ? 'боевой' : 'тестовый'}</td>
                                    <td>{r.hits} / {r.confirmed} / {r.falsePositive}</td>
                                    <td>{formatDateTime(r.updated)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </Sheet>
                    {selected && (
                        <Sheet variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 8 }}>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography level="title-lg" sx={{ flex: 1, minWidth: 160 }}>{selected.name}</Typography>
                                <HintButton
                                    size="sm"
                                    hint="Записывает имя, режим, приоритет, условия и флаги. Прогон работает по последней сохранённой версии условий."
                                    onClick={() => saveRule().catch(console.error)}
                                >
                                    Сохранить
                                </HintButton>
                                <HintButton
                                    size="sm"
                                    color="danger"
                                    variant="outlined"
                                    hint="Мягко удаляет правило. Новые попадания по нему больше не пишутся. Черновик просто закрывается."
                                    onClick={async () => {
                                        if (!selected.id) {
                                            setSelected(null);
                                            setBaseline('');
                                            return;
                                        }
                                        await autoHoldDeleteRuleApi(selected.id);
                                        setSelected(null);
                                        setBaseline('');
                                        await loadRules();
                                    }}
                                >
                                    Удалить
                                </HintButton>
                            </Stack>
                            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 8 }}>
                                <Typography level="title-sm" sx={{ mb: 1.5 }}>Общее</Typography>
                                <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="flex-end">
                                    <FormControl sx={{ minWidth: 220, flex: 1 }}>
                                        <FormLabel>Имя</FormLabel>
                                        <Input value={selected.name} onChange={e => setSelected({ ...selected, name: e.target.value })} />
                                    </FormControl>
                                    <FormControl sx={{ minWidth: 140 }}>
                                        <FormLabel>
                                            <FieldHint
                                                label="Режим"
                                                hint={
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                        <Typography level="body-sm">
                                                            Тестовый — только попадания. Блок не ставится, пока не нажмёте «применить». В очередь холдов не попадает.
                                                        </Typography>
                                                        <Typography level="body-sm">
                                                            Боевой — сразу вешает выбранные блокировки. Пользователь появляется во вкладке «Очередь», чтобы решить: оставить блок или снять автохолд.
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </FormLabel>
                                        <Select
                                            value={selected.mode}
                                            onChange={(_, v) => {
                                                if (v == null || Number(v) === selected.mode) {
                                                    return;
                                                }
                                                setSelected({ ...selected, mode: Number(v) });
                                            }}
                                        >
                                            <Option value={0}>тестовый</Option>
                                            <Option value={1}>боевой</Option>
                                        </Select>
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Включено</FormLabel>
                                        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 'var(--Input-minHeight, 2.25rem)' }}>
                                            <Checkbox
                                                checked={selected.enabled}
                                                onChange={e => setSelected({ ...selected, enabled: e.target.checked })}
                                            />
                                        </Box>
                                    </FormControl>
                                </Stack>
                            </Sheet>
                            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 8 }}>
                                <Typography level="title-sm" sx={{ mb: 1.5 }}>Условия срабатывания</Typography>
                                <FormControl sx={{ mb: 1.5 }}>
                                    <FormLabel>Условия:</FormLabel>
                                    <Typography level="body-sm" sx={{ fontFamily: 'var(--joy-fontFamily-code, monospace)' }}>
                                        {formula(root, signals)}
                                    </Typography>
                                </FormControl>
                                <AstEditor node={root} depth={0} signals={signals} onChange={setRoot} />
                            </Sheet>
                            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 8 }}>
                                <Typography level="title-sm" sx={{ mb: 1.5 }}>Результат срабатывания</Typography>
                                <FormControl>
                                    <FormLabel>При срабатывании блок на:</FormLabel>
                                    <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mt: 0.5 }}>
                                        {getBlockRuleLabels().map(r => (
                                            <Checkbox
                                                key={r.value}
                                                label={r.label}
                                                checked={(selected.actionFlags & r.value) === r.value}
                                                onChange={e => setSelected({
                                                    ...selected,
                                                    actionFlags: e.target.checked
                                                        ? selected.actionFlags | r.value
                                                        : selected.actionFlags & ~r.value,
                                                })}
                                            />
                                        ))}
                                    </Stack>
                                </FormControl>
                            </Sheet>
                            {selected.id && !isDirty() ? (
                            <>
                            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 8 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" spacing={1} sx={{ mb: 0.5 }}>
                                    <Typography level="title-sm">
                                        <FieldHint
                                            label="Кто попал в правило"
                                            hint={
                                                selected.mode === AutoHoldRuleMode.Test
                                                    ? 'Тестовый режим: блок сам не ставится, сюда только пишется попадание. «Заблокировать» вешает флаги. «Ошибка» — попадание ложное; снять флаги — «Да, и снять блокировку» в окне.'
                                                    : 'Боевой режим: при попадании блок уже повешен, человек мог уйти в «Очередь». Здесь журнал, кто уже попал.'
                                            }
                                        />
                                    </Typography>
                                    {selected.mode === AutoHoldRuleMode.Test && (
                                    <HintButton
                                        size="sm"
                                        variant="outlined"
                                        hint="Повесит выбранные блокировки на всех из таблицы, кому ещё не блокировали. Вайтлист будет пропущен."
                                        onClick={() => setApplyAllOpen(true)}
                                    >
                                        Заблокировать всех
                                    </HintButton>
                                    )}
                                </Stack>
                                <Typography level="body-sm" color="neutral" sx={{ mb: 1.5 }}>
                                    {selected.mode === AutoHoldRuleMode.Test
                                        ? 'Правило тестовое: живое событие или поиск ниже только записывает человека сюда. «Заблокировать» вешает флаги, «Ошибка» — ложное срабатывание (блок снимается отдельной кнопкой в окне).'
                                        : 'Правило боевое: блок ставится сам. Таблица — журнал, кто уже попал.'}
                                </Typography>
                                <Sheet variant="outlined">
                                    <Table>
                                        <thead>
                                        <tr>
                                            <th>Кто</th>
                                            <th>Когда попал</th>
                                            <th>Раз</th>
                                            <th>Почему</th>
                                            <th>Блок</th>
                                            <th></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {hits.map(h => (
                                            <tr key={String(h.userId)}>
                                                <td>{userLink(h.userId)}</td>
                                                <td>{formatDateTime(h.lastMatchedAt)}</td>
                                                <td>{h.hitCount}</td>
                                                <td><Typography level="body-xs">{h.explain}</Typography></td>
                                                <td>{flagsChips(h.appliedFlags)}</td>
                                                <td>
                                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    {h.verdict === AutoHoldCaseStatus.FalsePositive && (
                                                        <Chip size="sm" color="danger" variant="soft">ошибка</Chip>
                                                    )}
                                                    {h.verdict === AutoHoldCaseStatus.Confirmed && (
                                                        <Chip size="sm" color="success" variant="soft">блок оставили</Chip>
                                                    )}
                                                    {selected.mode === AutoHoldRuleMode.Test && !h.appliedAt && !h.verdict && (
                                                        <HintButton
                                                            size="sm"
                                                            hint="Повесит флаги из «Результат срабатывания» и пометит попадание как «блок оставили». В очередь разбора человек не попадёт."
                                                            onClick={() => setDecisionConfirm({
                                                                title: 'Заблокировать?',
                                                                body: `Повесим флаги этого правила на ${h.userId} и пометим как «блок оставили». В очередь разбора человек не попадёт.`,
                                                                run: async () => {
                                                                    await autoHoldApplyHitApi(selected.id, String(h.userId));
                                                                    await loadHits(selected.id);
                                                                    await loadRules();
                                                                },
                                                            })}
                                                        >
                                                            Заблокировать
                                                        </HintButton>
                                                    )}
                                                    {!h.verdict && (
                                                        <HintButton
                                                            size="sm"
                                                            variant="outlined"
                                                            hint="Попадание ложное. Блок по этому правилу снимем, только если в окне нажать «Да, и снять блокировку». Ручные блокировки не трогаем."
                                                            onClick={() => setDecisionConfirm({
                                                                title: 'Отметить как ошибку?',
                                                                body: `Пометим попадание ${h.userId} в это правило как ошибку. Ограничения останутся. Чтобы снять флаги этого правила — «Да, и снять блокировку».`,
                                                                run: async () => {
                                                                    await autoHoldHitFalsePositiveApi(selected.id, String(h.userId), false);
                                                                    await loadHits(selected.id);
                                                                    await loadRules();
                                                                },
                                                                extraLabel: 'Да, и снять блокировку',
                                                                extraRun: async () => {
                                                                    await autoHoldHitFalsePositiveApi(selected.id, String(h.userId), true);
                                                                    await loadHits(selected.id);
                                                                    await loadRules();
                                                                },
                                                            })}
                                                        >
                                                            Ошибка
                                                        </HintButton>
                                                    )}
                                                    </Stack>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </Sheet>
                                {hits.length === 0 && (
                                    <Typography level="body-sm" sx={{ mt: 1 }} color="neutral">
                                        Пока никто не попал. Дождитесь пополнения или покупки — или прогоните SteamId в блоке «Поиск по правилу» ниже.
                                    </Typography>
                                )}
                            </Sheet>
                            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 8 }}>
                                <Typography level="title-sm" sx={{ mb: 0.5 }}>
                                    <FieldHint
                                        label="Поиск по правилу"
                                        hint="Проверяет только это сохранённое правило. Остальные не трогаем. Блок не ставится: кто попал — появится в таблице выше, чтобы при необходимости заблокировать вручную."
                                    />
                                </Typography>
                                <Typography level="body-sm" color="neutral" sx={{ mb: 1.5 }}>
                                    Можно проверить конкретных людей или всех, у кого уже стоит любая блокировка.
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="flex-end" flexWrap="wrap" sx={{ mb: 1.5 }}>
                                    <FormControl sx={{ flex: 1, minWidth: 260 }}>
                                        <FormLabel>
                                            <FieldHint
                                                label="SteamId"
                                                hint="17 цифр профиля Steam (7656119…). Можно вставить ссылку на профиль или несколько id — через запятую, пробел или с новой строки. Enter запускает проверку."
                                            />
                                        </FormLabel>
                                        <Input
                                            placeholder="76561198000000000"
                                            value={previewText}
                                            onChange={e => setPreviewText(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    void runPreview();
                                                }
                                            }}
                                            sx={{ fontFamily: 'var(--joy-fontFamily-code, monospace)' }}
                                        />
                                    </FormControl>
                                    <HintButton
                                        hint="Проверит сохранённые условия только этого правила на этих SteamId. Блок не ставится."
                                        onClick={() => runPreview().catch(console.error)}
                                    >
                                        Прогнать
                                    </HintButton>
                                    <HintButton
                                        variant="outlined"
                                        hint="Проверит только это правило на всех, кто уже в блокировках. Флаги не вешаются."
                                        onClick={() => setRunAllOpen(true)}
                                    >
                                        Прогнать по заблокированным
                                    </HintButton>
                                </Stack>
                                {preview.some(p => !p.valid || !p.matched) && (
                                    <Sheet variant="soft" sx={{ p: 1, borderRadius: 8 }}>
                                        <Typography level="body-sm" sx={{ mb: 0.5 }}>Не прошли проверку</Typography>
                                        <Table size="sm">
                                            <tbody>
                                            {preview.filter(p => !p.valid || !p.matched).map((p, i) => (
                                                <tr key={i}>
                                                    <td>{p.valid && p.userId ? userLink(p.userId) : p.raw}</td>
                                                    <td>{p.valid ? 'не попал' : (p.error ?? 'неверный SteamId')}</td>
                                                    <td><Typography level="body-xs">{p.explain}</Typography></td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </Table>
                                    </Sheet>
                                )}
                            </Sheet>
                            </>
                            ) : (
                                <Typography level="body-sm" color="neutral">
                                    Сохраните правило, чтобы увидеть, кто в него попал, и прогнать поиск.
                                </Typography>
                            )}
                        </Stack>
                        </Sheet>
                    )}
                            </>
                        ),
                    },
                    {
                        tabSlug: 'autohold-whitelist',
                        tabTitle: 'Вайтлист',
                        tabContent: (
                            <>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="flex-end">
                        <FormControl>
                            <FormLabel>
                                <FieldHint
                                    label="SteamId"
                                    hint="17 цифр профиля Steam (7656119…). Можно вставить ссылку на профиль."
                                />
                            </FormLabel>
                            <Input
                                placeholder="76561198000000000"
                                value={wlUser}
                                onChange={e => setWlUser(e.target.value)}
                                sx={{ fontFamily: 'var(--joy-fontFamily-code, monospace)', minWidth: 220 }}
                            />
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Причина</FormLabel>
                            <Input placeholder="Почему не трогаем" value={wlReason} onChange={e => setWlReason(e.target.value)} />
                        </FormControl>
                        <HintButton
                            hint="Автохолд не будет вешать флаги на этого пользователя, даже если условие сработает."
                            onClick={async () => {
                            await autoHoldAddWhitelistApi(normalizeSteamIds(wlUser) || wlUser, wlReason);
                            await loadWhitelist();
                        }}>Добавить</HintButton>
                    </Stack>
                    <Sheet variant="outlined">
                        <Table>
                            <thead>
                            <tr><th>SteamId</th><th>Причина</th><th>Кто</th><th></th></tr>
                            </thead>
                            <tbody>
                            {whitelist.map(w => (
                                <tr key={w.id}>
                                    <td>{userLink(w.userId)}</td>
                                    <td>{w.reason}</td>
                                    <td>{w.addedBy}</td>
                                    <td>
                                        <Button size="sm" color="danger" variant="plain" onClick={async () => {
                                            await autoHoldRemoveWhitelistApi(w.id);
                                            await loadWhitelist();
                                        }}>Удалить</Button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </Sheet>
                            </>
                        ),
                    },
                    {
                        tabSlug: 'autohold-dictionaries',
                        tabTitle: 'Справочники',
                        tabContent: (
                            <>
                    <Typography level="title-sm" sx={{ mb: 0.5 }}>
                        <FieldHint
                            label="Справочники для условий"
                            hint="Общие списки, на которые опираются условия правил. Меняете список здесь — при следующей проверке правила уже смотрят новые значения, выпускать новую версию программы не нужно. Сохраняйте каждый список отдельно."
                        />
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 2 }}>
                        Каждое значение — с новой строки. Пустые строки отбрасываются. Порядок строк не важен. Удалить значение — стереть строку и сохранить.
                    </Typography>
                    {dicts.length === 0 && (
                        <Typography level="body-sm" color="neutral">
                            Списков пока нет. Они появляются после обновления базы.
                        </Typography>
                    )}
                    {dicts.filter(d => d.key === 'hostingAsns' || d.key === 'orgKeywords' || d.key === 'fiatCurrencies').map(d => {
                        const meta = dictionaryMeta(d.key);
                        return (
                            <Sheet key={d.key} variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 8 }}>
                                <FormControl>
                                    <FormLabel>
                                        <FieldHint label={meta.title} hint={meta.hint} />
                                    </FormLabel>
                                    <Typography level="body-sm" sx={{ mt: 0.5 }}>
                                        Где используется: {meta.usedIn}
                                    </Typography>
                                    <Typography level="body-sm" sx={{ mb: 0.75 }}>
                                        Для чего: {meta.purpose}
                                    </Typography>
                                    <Typography level="body-xs" sx={{ mb: 1 }} color="neutral">
                                        {meta.format}
                                    </Typography>
                                    <Textarea
                                        minRows={3}
                                        placeholder={meta.placeholder}
                                        value={d.values.join('\n')}
                                        onChange={e => setDicts(dicts.map(x => x.key === d.key ? { ...x, values: e.target.value.split('\n') } : x))}
                                    />
                                    <HintButton
                                        size="sm"
                                        sx={{ mt: 1, alignSelf: 'flex-start' }}
                                        hint="Записывает только этот список. Остальные не трогает. Правила увидят новые значения на следующей проверке, не сразу у уже открытой карточки пользователя."
                                        onClick={async () => {
                                            const values = d.values.map(v => v.trim()).filter(Boolean);
                                            await autoHoldSaveDictionaryApi(d.key, values, d.description);
                                            setDicts(dicts.map(x => x.key === d.key ? { ...x, values } : x));
                                            NotificationService.alertSuccess('Список сохранён');
                                        }}
                                    >
                                        Сохранить
                                    </HintButton>
                                </FormControl>
                            </Sheet>
                        );
                    })}
                            </>
                        ),
                    },
                ]}
            />
            <ConfirmDialog
                title={decisionConfirm?.title ?? ''}
                open={decisionConfirm != null}
                setOpen={open => {
                    if (!open) {
                        setDecisionConfirm(null);
                    }
                }}
                onConfirm={() => decisionConfirm?.run()}
                extraConfirmLabel={decisionConfirm?.extraLabel}
                onExtraConfirm={decisionConfirm?.extraRun}
            >
                {decisionConfirm?.body ?? ''}
            </ConfirmDialog>
            <ConfirmDialog
                title="Заблокировать всех, кто попал?"
                open={applyAllOpen}
                setOpen={setApplyAllOpen as unknown as (e: boolean) => void}
                onConfirm={() => {
                    if (!selected) {
                        return;
                    }
                    void autoHoldApplyAllApi(selected.id).then(() => {
                        NotificationService.alertSuccess('Задача поставлена');
                    });
                }}
            >
                {`Повесить флаги этого правила на ${hits.filter(h => !h.appliedAt && !h.verdict).length} человек из таблицы. Вайтлист будет пропущен.`}
            </ConfirmDialog>
            <ConfirmDialog
                title="Прогнать это правило по всем заблокированным?"
                open={runAllOpen}
                setOpen={setRunAllOpen as unknown as (e: boolean) => void}
                onConfirm={() => {
                    if (!selected) {
                        return;
                    }
                    void autoHoldPreviewAllApi(selected.id).then(() => {
                        NotificationService.alertSuccess('Прогон поставлен в очередь');
                    });
                }}
            >
                Оценит только выделенное правило, без применения флагов. Остальные правила не трогаем. Попадания появятся в таблице, когда очередь доработает.
            </ConfirmDialog>
            <ConfirmDialog
                title="Сохранить изменения?"
                open={unsavedOpen}
                setOpen={setUnsavedOpen as unknown as (e: boolean) => void}
                onConfirm={() => finishPendingNav(true)}
                onDecline={() => finishPendingNav(false)}
            >
                {`В «${unsavedRuleName}» есть несохранённые правки. Сохранить перед продолжением?`}
            </ConfirmDialog>
        </Box>
    );
}
